export const MAIL_DOMAINS = ["ccnote.me"] as const;

/** Characters that separate the username from the category: aaron#shopping@ / aaron+shopping@ */
export const CATEGORY_SEPARATORS = ["#", "+"] as const;

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 30;
export const CATEGORY_MAX = 32;
/** Most categories (#tags) one note can have. */
export const NOTE_CATEGORIES_MAX = 10;

export const NOTE_SOURCES = [
  "email",
  "web",
  "extension",
  "x",
  "x_bot",
  "wecom",
  "mobile",
  "share",
  "xhs",
  "reddit",
] as const;
export type NoteSource = (typeof NOTE_SOURCES)[number];

export const NOTE_FORMATS = ["text", "markdown"] as const;
export type NoteFormat = (typeof NOTE_FORMATS)[number];

export const NOTE_STATUSES = ["active", "pending", "archived", "trashed"] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];

/**
 * Role mailboxes that belong to the ccnote team (RFC 2142 plus common contact names).
 * Mail to these is forwarded to the owner instead of becoming a note. Some are shorter
 * than USERNAME_MIN (e.g. "hi"), so they are matched before username validation.
 */
export const ROLE_MAILBOXES = new Set([
  // RFC 2142 / infrastructure
  "postmaster", "abuse", "hostmaster", "webmaster", "security", "noc",
  // contact
  "support", "help", "hello", "hi", "contact", "info", "feedback", "team",
  // business
  "admin", "billing", "legal", "privacy", "press", "sales", "marketing", "partners", "jobs",
]);

/** Local parts that can never be registered as usernames (role mailboxes are included automatically). */
export const RESERVED_USERNAMES = new Set([
  ...ROLE_MAILBOXES,
  // mail system
  "mailer-daemon", "daemon", "noreply", "no-reply", "donotreply", "do-not-reply", "bounce", "bounces",
  "mail", "email", "smtp", "imap", "pop", "pop3", "mx", "dmarc", "dkim", "spf", "list", "lists",
  "listserv", "majordomo", "notify", "notifications", "alerts", "newsletter", "unsubscribe",
  // system / infrastructure
  "root", "system", "sysadmin", "administrator", "operator", "server", "www", "web", "api", "app",
  "apps", "cdn", "static", "assets", "dev", "test", "testing", "staging", "prod", "status", "ftp",
  "ssl", "localhost", "null", "undefined", "anonymous", "nobody", "guest", "user", "users",
  // product / brand
  "ccnote", "ccnotes", "note", "notes", "official", "staff", "moderator", "mod", "owner", "bot",
  "me", "you", "account", "accounts", "login", "signin", "signup", "register", "auth", "oauth",
  "settings", "download", "downloads", "blog", "docs", "about", "pricing", "store", "shop",
  "inbox", "pending", "trash", "archive", "all", "everyone", "everybody",
  // trust & safety
  "compliance", "trust", "safety", "verify", "verification", "report", "reports", "spam", "phishing",
  "fraud", "copyright", "dmca", "gdpr",
  // company roles
  "ceo", "cto", "cfo", "founder", "hr", "careers", "investors", "media", "office",
]);

/**
 * Prefixes nobody may register, so "ccnote-team", "admin2" or "support.cn" can't impersonate us.
 * Matched after removing . _ - from the name.
 */
export const RESERVED_PREFIXES = [
  "ccnote", "admin", "support", "official", "staff", "security", "postmaster", "webmaster",
  "hostmaster", "noreply", "donotreply", "mailerdaemon", "moderator", "system", "root",
] as const;
